import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Paper, CircularProgress, Chip, Alert, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axios from '../api/client';
import { BASE_RULES, validateRules } from '../utils/GroupingRules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8004';

const RevisionResolverDialog = ({ open, onClose, group, onSaved, onDeleteGroup }) => {
  const [loading, setLoading] = useState(true);
  const [diffData, setDiffData] = useState(null);
  const [error, setError] = useState(null);
  
  // State for which unmatched items to keep vs discard
  const [discardUnmatchedIds, setDiscardUnmatchedIds] = useState(new Set());
  
  // State for proposed group validation
  const [violations, setViolations] = useState([]);

  useEffect(() => {
    if (open && group) {
      setLoading(true);
      setError(null);
      setDiscardUnmatchedIds(new Set());
      
      axios.get(`${API}/api/line-item-groups/${group.id}/revision-diff`)
        .then(res => {
          setDiffData(res.data);
          // By default, discard all unmatched (cancelled) items
          const unmatchedIds = new Set(res.data.unmatched.map(u => u.id));
          setDiscardUnmatchedIds(unmatchedIds);
        })
        .catch(err => {
          console.error(err);
          setError("Failed to fetch revision diff.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, group]);

  // Re-run validation whenever discard selection changes
  useEffect(() => {
    if (!diffData || !group) return;
    
    // Compute proposed new group items
    const newItems = diffData.diffs.map(d => d.new_item);
    const keptUnmatched = diffData.unmatched.filter(u => !discardUnmatchedIds.has(u.id));
    
    const proposedItems = [...newItems, ...keptUnmatched];
    
    const rulesToUse = group.custom_rules && group.custom_rules.length > 0 ? group.custom_rules : BASE_RULES;
    const v = validateRules(proposedItems, rulesToUse);
    setViolations(v);
    
  }, [diffData, discardUnmatchedIds, group]);

  const toggleDiscard = (id) => {
    const next = new Set(discardUnmatchedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDiscardUnmatchedIds(next);
  };

  const handleAccept = async () => {
    if (!diffData || !group) return;
    
    const newItems = diffData.diffs.map(d => d.new_item);
    const keptUnmatched = diffData.unmatched.filter(u => !discardUnmatchedIds.has(u.id));
    const finalItems = [...newItems, ...keptUnmatched];
    
    try {
      setLoading(true);
      await axios.put(`${API}/api/line-item-groups/${group.id}`, {
        group_name: group.group_name,
        line_item_ids: finalItems.map(i => i.id),
        group_data: finalItems,
        custom_rules: group.custom_rules
      });
      onSaved(group.id);
    } catch (err) {
      console.error(err);
      setError("Failed to update group.");
      setLoading(false);
    }
  };

  if (!open || !group) return null;

  const hasUpdates = diffData?.has_updates;
  const noChangesButScanned = diffData && !diffData.has_updates;

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CompareArrowsIcon color="primary" /> PO Revision Resolver: {group.group_name}
      </DialogTitle>
      
      <DialogContent dividers sx={{ bgcolor: '#fbfbfb' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : noChangesButScanned ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            New PO found, but no changes were detected for the items in this group. You can safely ignore this alert.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            <Alert severity="info">
              A newer PO document was uploaded that affects items in this group. Review the changes below.
            </Alert>

            {/* Changed Items */}
            {diffData.diffs.filter(d => d.has_changes).length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Updated Items</Typography>
                {diffData.diffs.filter(d => d.has_changes).map((diff, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ mb: 1, p: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      PO {diff.new_item.customer_po_number} | Style {diff.new_item.customer_style_number} | {diff.new_item.color} - {diff.new_item.size}
                    </Typography>
                    
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell sx={{ bgcolor: 'rgba(211,47,47,0.05)', color: 'error.main' }}>Old Value</TableCell>
                          <TableCell sx={{ bgcolor: 'rgba(46,125,50,0.05)', color: 'success.main' }}>New Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(diff.changes).map(([field, vals]) => (
                          <TableRow key={field}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{field.replace(/_/g, ' ')}</TableCell>
                            <TableCell sx={{ color: 'error.main', textDecoration: 'line-through' }}>{String(vals.old ?? '—')}</TableCell>
                            <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{String(vals.new ?? '—')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Unmatched / Cancelled Items */}
            {diffData.unmatched.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color="error.main" gutterBottom>
                  Cancelled / Removed Items
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  These items were in the old PO but are missing from the newly uploaded revision.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {diffData.unmatched.map((u, idx) => {
                    const isDiscarded = discardUnmatchedIds.has(u.id);
                    return (
                      <Paper key={idx} variant="outlined" sx={{ 
                        p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderColor: isDiscarded ? 'error.light' : 'divider',
                        bgcolor: isDiscarded ? 'rgba(211,47,47,0.05)' : 'background.paper'
                      }}>
                        <Box>
                          <Typography variant="body2" sx={{ textDecoration: isDiscarded ? 'line-through' : 'none' }}>
                            PO {u.customer_po_number} | Style {u.customer_style_number} | {u.color} - {u.size} (UPC: {u.upc || 'N/A'})
                          </Typography>
                        </Box>
                        <Button 
                          size="small" 
                          color={isDiscarded ? "primary" : "error"}
                          variant={isDiscarded ? "text" : "contained"}
                          onClick={() => toggleDiscard(u.id)}
                          startIcon={isDiscarded ? null : <DeleteIcon />}
                        >
                          {isDiscarded ? "Keep Item Anyway" : "Discard Item"}
                        </Button>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Validation Violations */}
            {violations.length > 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">Group Rule Violations Detected</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The new data violates the configured grouping rules. You must either discard the group or resolve the issues.
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {violations.map((v, i) => <li key={i}><Typography variant="caption">{v}</Typography></li>)}
                </ul>
              </Alert>
            )}

          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          <Button color="error" onClick={() => onDeleteGroup(group)}>Disband Group</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAccept}
            disabled={loading || !hasUpdates || violations.length > 0}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {violations.length > 0 ? "Cannot Accept (Rules Violated)" : "Accept & Update Group"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionResolverDialog;
